import { _ModuleSupport } from 'ag-charts-community';
import type { DistantObject, Geometry, Position } from 'ag-charts-core';
import { SceneChangeDetection, SceneObjectChangeDetection, objectsEqual } from 'ag-charts-core';

import { lineStringDistance } from './lineStringUtil';
import { polygonDistance } from './polygonUtil';

const { Path, ExtendedPath2D, BBox } = _ModuleSupport;

export enum GeoGeometryRenderMode {
    All = 0b11,
    Polygons = 0b01,
    Lines = 0b10,
}

export class GeoGeometry<D = unknown> extends Path<D> implements DistantObject {
    @SceneObjectChangeDetection({ equals: objectsEqual })
    projectedGeometry: Geometry | undefined = undefined;

    @SceneChangeDetection()
    renderMode: GeoGeometryRenderMode = GeoGeometryRenderMode.All;

    private bbox: _ModuleSupport.BBox | undefined;
    // Keep non-filled shapes separate so we don't fill them
    private readonly strokePath = new ExtendedPath2D();

    protected override computeBBox(): _ModuleSupport.BBox | undefined {
        if (this.dirtyPath || this.isDirtyPath()) {
            this.updatePath();
            this.dirtyPath = false;
        }

        return this.bbox?.clone();
    }

    override updatePath(): void {
        const { projectedGeometry } = this;

        this.strokePath.clear();
        this.path.clear();

        this.bbox = projectedGeometry == null ? undefined : this.drawGeometry(projectedGeometry, undefined);
    }

    override drawPath(ctx: any) {
        super.drawPath(ctx);

        this.renderStroke(ctx, this.strokePath.getPath2D());
    }

    override containsPoint(x: number, y: number): boolean {
        const { projectedGeometry } = this;
        if (projectedGeometry == null) return false;

        if (!this.getBBox().containsPoint(x, y)) return false;

        return this.geometryDistance(projectedGeometry, x, y) <= 0;
    }

    override distanceSquared(x: number, y: number): number {
        const { projectedGeometry } = this;
        if (projectedGeometry == null) return Infinity;
        const distance = this.geometryDistance(projectedGeometry, x, y);
        return distance > 0 ? distance * distance : 0;
    }

    private geometryDistance(geometry: Geometry, x: number, y: number): number {
        const { renderMode, strokeWidth } = this;
        const drawPolygons = (renderMode & GeoGeometryRenderMode.Polygons) !== 0;
        const drawLines = (renderMode & GeoGeometryRenderMode.Lines) !== 0;
        const minStrokeDistance = Math.max(strokeWidth / 2, 1) + 1;

        switch (geometry.type) {
            case 'GeometryCollection':
                return geometry.geometries.reduce(
                    (minDistance, g) => Math.min(minDistance, this.geometryDistance(g, x, y)),
                    Infinity
                );
            case 'MultiPolygon':
                return drawPolygons
                    ? geometry.coordinates.reduce(
                          (minDistance, polygon) => Math.min(minDistance, Math.max(polygonDistance(polygon, x, y), 0)),
                          Infinity
                      )
                    : Infinity;
            case 'Polygon':
                return drawPolygons ? Math.max(polygonDistance(geometry.coordinates, x, y), 0) : Infinity;
            case 'MultiLineString':
                return drawLines
                    ? geometry.coordinates.reduce((minDistance, lineString) => {
                          return Math.min(
                              minDistance,
                              Math.max(lineStringDistance(lineString, x, y) - minStrokeDistance, 0)
                          );
                      }, Infinity)
                    : Infinity;
            case 'LineString':
                return drawLines
                    ? Math.max(lineStringDistance(geometry.coordinates, x, y) - minStrokeDistance, 0)
                    : Infinity;
            case 'MultiPoint':
            case 'Point':
            default:
                return Infinity;
        }
    }

    private shouldDrawPolygons(): boolean {
        return (this.renderMode & GeoGeometryRenderMode.Polygons) !== 0;
    }

    private shouldDrawLines(): boolean {
        return (this.renderMode & GeoGeometryRenderMode.Lines) !== 0;
    }

    private drawGeometryCollection(
        geometries: Geometry[],
        bbox: _ModuleSupport.BBox | undefined
    ): _ModuleSupport.BBox | undefined {
        for (const g of geometries) {
            bbox = this.drawGeometry(g, bbox);
        }
        return bbox;
    }

    private drawMultiPolygon(
        coordinates: Position[][][],
        bbox: _ModuleSupport.BBox | undefined
    ): _ModuleSupport.BBox | undefined {
        if (!this.shouldDrawPolygons()) return bbox;

        for (const polygon of coordinates) {
            bbox = this.drawPolygon(this.path, polygon, bbox);
        }
        return bbox;
    }

    private drawSinglePolygon(
        coordinates: Position[][],
        bbox: _ModuleSupport.BBox | undefined
    ): _ModuleSupport.BBox | undefined {
        if (!this.shouldDrawPolygons()) return bbox;
        return this.drawPolygon(this.path, coordinates, bbox);
    }

    private drawMultiLineString(
        coordinates: Position[][],
        bbox: _ModuleSupport.BBox | undefined
    ): _ModuleSupport.BBox | undefined {
        if (!this.shouldDrawLines()) return bbox;

        for (const lineString of coordinates) {
            bbox = this.drawLineString(this.strokePath, lineString, bbox, false);
        }
        return bbox;
    }

    private drawSingleLineString(
        coordinates: Position[],
        bbox: _ModuleSupport.BBox | undefined
    ): _ModuleSupport.BBox | undefined {
        if (!this.shouldDrawLines()) return bbox;
        return this.drawLineString(this.strokePath, coordinates, bbox, false);
    }

    private drawGeometry(geometry: Geometry, bbox: _ModuleSupport.BBox | undefined): _ModuleSupport.BBox | undefined {
        switch (geometry.type) {
            case 'GeometryCollection':
                return this.drawGeometryCollection(geometry.geometries, bbox);
            case 'MultiPolygon':
                return this.drawMultiPolygon(geometry.coordinates, bbox);
            case 'Polygon':
                return this.drawSinglePolygon(geometry.coordinates, bbox);
            case 'MultiLineString':
                return this.drawMultiLineString(geometry.coordinates, bbox);
            case 'LineString':
                return this.drawSingleLineString(geometry.coordinates, bbox);
            case 'Point':
            case 'MultiPoint':
                return bbox;
        }
    }

    private drawPolygon(
        path: _ModuleSupport.ExtendedPath2D,
        polygons: Position[][],
        bbox: _ModuleSupport.BBox | undefined
    ): _ModuleSupport.BBox | undefined {
        if (polygons.length < 1) return bbox;

        bbox = this.drawLineString(path, polygons[0], bbox, true);
        for (let i = 1; i < polygons.length; i += 1) {
            // Don't extend bbox for enclaves
            const enclave = polygons[i];
            this.drawLineString(path, enclave, undefined, true);
        }
        return bbox;
    }

    private drawLineString(
        path: _ModuleSupport.ExtendedPath2D,
        coordinates: Position[],
        bbox: _ModuleSupport.BBox | undefined,
        isClosed: boolean
    ): _ModuleSupport.BBox | undefined {
        if (coordinates.length < 2) return bbox;

        // For polygons (i.e. closed), the start and end coordinates are the same
        // Use closePath instead so the path draws its miters correctly
        const end = isClosed ? coordinates.length - 1 : coordinates.length;

        for (let i = 0; i < end; i += 1) {
            const [x, y] = coordinates[i];

            if (i === 0) {
                path.moveTo(x, y);
            } else {
                path.lineTo(x, y);
            }

            if (bbox == null) {
                bbox = new BBox(x, y, 0, 0);
            } else {
                const { x: x0, y: y0 } = bbox;
                const x1 = x0 + bbox.width;
                const y1 = y0 + bbox.height;

                bbox.x = Math.min(x0, x);
                bbox.y = Math.min(y0, y);
                bbox.width = Math.max(x1, x) - bbox.x;
                bbox.height = Math.max(y1, y) - bbox.y;
            }
        }

        if (isClosed) {
            path.closePath();
        }

        return bbox;
    }
}
